import React, { useEffect, useState } from "react";
import InvoiceHeader from "../../components/invoice/InvoiceHeader";
import LogoUpload from "../../components/invoice/LogoUpload";
import BusinessDetailsForm from "../../components/invoice/BusinessDetailsForm";
import ClientDetailsForm from "../../components/invoice/ClientDetailsForm";
import InvoiceDetails from "../../components/invoice/InvoiceDetails";
import InvoiceItems from "../../components/invoice/InvoiceItems";
import InvoiceTotals from "../../components/invoice/InvoiceTotals";
import NotesSection from "../../components/invoice/NotesSection";
import BankingDetailsForm from "../../components/invoice/BankingDetailsForm";
import "./style.css";

const initialBusinessDetails = {
	name: "",
	email: "",
	address: "",
	phone: "",
	businessNumber: "",
	website: "",
	owner: ""
};

const initialClientDetails = {
	name: "",
	email: "",
	address: "",
	phone: "",
	mobile: "",
	fax: ""
};

const initialBankingDetails = {
	bankName: "",
	accountName: "",
	accountNumber: "",
	routingNumber: "",
	swiftCode: ""
};

const initialItem = {
	id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
	name: "",
	description: "",
	rate: 0,
	qty: 1,
	amount: 0
};

export default function InvoicePage() {
	const [invoiceData, setInvoiceData] = useState({
		logo: null,
		from: initialBusinessDetails,
		billTo: initialClientDetails,
		invoiceNumber: "",
		date: new Date(),
		terms: "net-30",
		items: [initialItem],
		notes: "",
		banking: initialBankingDetails
	});

	const handlePrint = () => {
		document.body.classList.add("preview-mode");
		// // Force layout so styles apply before print dialog
		void document.body.offsetHeight;
		window.print();
	};

	useEffect(() => {
		const cleanup = () => document.body.classList.remove("preview-mode");

		window.addEventListener("afterprint", cleanup);

		// Fallback: some browsers don’t reliably fire afterprint on cancel
		const onFocus = () => cleanup();
		window.addEventListener("focus", onFocus);

		return () => {
			window.removeEventListener("afterprint", cleanup);
			window.removeEventListener("focus", onFocus);
		};
	}, []);

	return (
		<>
			{/* Fixed Action Buttons - hidden when printing */}
			<div className="actions print-hide">
				<button type="button" className="btn btn-primary" onClick={handlePrint}>
					<span className="btn-icon" aria-hidden="true">
						{/* Printer icon */}
						<svg viewBox="0 0 24 24" width="16" height="16">
							<path d="M7 2h10a2 2 0 0 1 2 2v4h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-3v2a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-2H2a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2h1V4a2 2 0 0 1 2-2Zm10 6V4H7v4h10Zm0 12v-6H7v6h10Zm3-4h2v-6h-2v2a1 1 0 1 1-2 0V10H6v2a1 1 0 1 1-2 0V10H2v6h2v-1a1 1 0 1 1 2 0v3h12v-3a1 1 0 1 1 2 0v1Z" />
						</svg>
					</span>
					Print Invoice
				</button>

				<button type="button" className="btn btn-outline" onClick={handlePrint}>
					<span className="btn-icon" aria-hidden="true">
						{/* Download icon */}
						<svg viewBox="0 0 24 24" width="16" height="16">
							<path d="M12 3a1 1 0 0 1 1 1v9.6l2.3-2.3a1 1 0 1 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L11 13.6V4a1 1 0 0 1 1-1Zm-7 14a1 1 0 0 1 1 1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-1a1 1 0 1 1 2 0v1a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-1a1 1 0 0 1 1-1Z" />
						</svg>
					</span>
					Save as PDF
				</button>
			</div>

			<div className="page">
				<div className="container">
					{/* Header with Logo */}
					<div className="header-row">
						<InvoiceHeader />
						<LogoUpload
							logo={invoiceData.logo}
							onLogoChange={(logo) => setInvoiceData((prev) => ({ ...prev, logo }))}
						/>
					</div>

					<div className="stack">
						{/* From and Bill To */}
						<div className="grid-2">
							<BusinessDetailsForm
								data={invoiceData.from}
								onChange={(from) => setInvoiceData((prev) => ({ ...prev, from }))}
							/>
							<ClientDetailsForm
								data={invoiceData.billTo}
								onChange={(billTo) => setInvoiceData((prev) => ({ ...prev, billTo }))}
							/>
						</div>

						{/* Invoice Details */}
						<InvoiceDetails
							invoiceNumber={invoiceData.invoiceNumber}
							date={invoiceData.date}
							terms={invoiceData.terms}
							onInvoiceNumberChange={(invoiceNumber) =>
								setInvoiceData((prev) => ({ ...prev, invoiceNumber }))
							}
							onDateChange={(date) => setInvoiceData((prev) => ({ ...prev, date }))}
							onTermsChange={(terms) => setInvoiceData((prev) => ({ ...prev, terms }))}
						/>

						{/* Items */}
						<InvoiceItems
							items={invoiceData.items}
							onItemsChange={(items) => setInvoiceData((prev) => ({ ...prev, items }))}
						/>

						{/* Totals */}
						<InvoiceTotals items={invoiceData.items} />

						{/* Notes and Banking */}
						<div className="grid-2">
							<NotesSection
								notes={invoiceData.notes}
								onNotesChange={(notes) => setInvoiceData((prev) => ({ ...prev, notes }))}
							/>
							<BankingDetailsForm
								data={invoiceData.banking}
								onChange={(banking) => setInvoiceData((prev) => ({ ...prev, banking }))}
							/>
						</div>
					</div>

					{/* Footer */}
					<footer className="invoice-footer print-hide">
						<p>Powered by <a href="https://mftechs.co.za" target="_blank" rel="noopener noreferrer">Moonface Techs</a> • Create professional invoices instantly</p>
					</footer>
				</div>
			</div>
		</>
	);
}
